import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, CheckCircle, Loader2, Truck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface BookLoadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  load: {
    loadId?: string;
    originCity: string;
    originState: string;
    destCity: string;
    destState: string;
    rate: number;
    miles: number;
    equipment: string;
    pickupDate: string;
    dropDate: string;
    weight: number;
    company: string;
  };
}

export default function BookLoadDialog({ open, onOpenChange, load }: BookLoadDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<{ name: string; mc_number?: string | null; dot_number?: string | null } | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    supabase
      .from("company_users")
      .select("company_id, companies:company_id(name, mc_number, dot_number)")
      .eq("user_id", user.id)
      .eq("is_active", true)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.companies) {
          const c = data.companies as any;
          setCompanyInfo({ name: c.name, mc_number: c.mc_number, dot_number: c.dot_number });
        }
      });
  }, [open, user]);

  const handleBook = async () => {
    if (!user || !load.loadId) return;
    setSubmitting(true);

    try {
      const { data: cuData } = await supabase
        .from("company_users")
        .select("company_id")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .limit(1)
        .maybeSingle();

      if (!cuData?.company_id) {
        toast({ variant: "destructive", title: "No company found", description: "You must belong to an active company to book loads." });
        setSubmitting(false);
        return;
      }

      const { data, error } = await supabase.rpc("carrier_accept_load", {
        p_load_id: load.loadId,
        p_carrier_company_id: cuData.company_id,
        p_rate_amount: load.rate,
        p_expires_minutes: 30,
      });

      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (!result?.success) {
        toast({ variant: "destructive", title: "Cannot book load", description: result?.error || "Failed to book load" });
        setSubmitting(false);
        return;
      }

      toast({ title: "Load booked!", description: "The broker has been notified. You'll receive confirmation shortly." });
      await queryClient.invalidateQueries({ queryKey: ["carrier-offers"] });
      await queryClient.invalidateQueries({ queryKey: ["carrier-offers-full"] });
      onOpenChange(false);
    } catch (err: any) {
      console.error("Book load error:", err);
      toast({ variant: "destructive", title: "Error", description: err.message || "Failed to book load" });
    } finally {
      setSubmitting(false);
    }
  };

  const hasRate = !!load.rate && load.rate > 0;
  const rpm = load.miles > 0 && hasRate ? (load.rate / load.miles).toFixed(2) : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-brand-teal" />
            Book Load
          </DialogTitle>
          <DialogDescription>
            Accept the posted rate and confirm booking. The broker will be notified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Load summary */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-sm font-semibold">
              <span>{load.originCity}, {load.originState}</span>
              <ArrowRight className="h-3.5 w-3.5 text-brand-teal" />
              <span>{load.destCity}, {load.destState}</span>
            </div>
            <div className="grid grid-cols-2 gap-y-1 text-xs">
              <span className="text-muted-foreground">Equipment</span>
              <span className="font-medium text-right">{load.equipment}</span>
              <span className="text-muted-foreground">Distance</span>
              <span className="font-medium text-right">{load.miles.toLocaleString()} mi</span>
              <span className="text-muted-foreground">Weight</span>
              <span className="font-medium text-right">{load.weight.toLocaleString()} lbs</span>
              <span className="text-muted-foreground">Pickup</span>
              <span className="font-medium text-right">{load.pickupDate}</span>
              <span className="text-muted-foreground">Delivery</span>
              <span className="font-medium text-right">{load.dropDate}</span>
            </div>
          </div>

          {/* Rate confirmation */}
          {hasRate ? (
            <div className="bg-success/5 border border-success/20 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">You're accepting the posted rate</p>
              <p className="text-2xl font-bold text-foreground font-mono">${load.rate.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">${rpm}/mi</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-sm font-semibold text-amber-900">No posted rate</p>
              <p className="text-xs text-amber-800 mt-1">This broker did not post a rate. Close and use <span className="font-semibold">Submit Rate</span> to send your own.</p>
            </div>
          )}

          {/* Your company info */}
          {companyInfo && (
            <div className="border border-border rounded-lg p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Your Company</p>
              <p className="text-sm font-semibold">{companyInfo.name}</p>
              <div className="flex gap-2 mt-1">
                {companyInfo.mc_number && <Badge variant="outline" className="text-[10px]">MC #{companyInfo.mc_number}</Badge>}
                {companyInfo.dot_number && <Badge variant="outline" className="text-[10px]">DOT #{companyInfo.dot_number}</Badge>}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleBook} disabled={submitting || !load.loadId || !hasRate} className="bg-brand-teal text-white hover:bg-brand-teal/90 gap-1.5">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Confirm Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
